import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import ProductCard from "./components/ProductCard";
import { API_BASE_URL } from "../../config/api";

const ProductList = () => {
  const { categoryType, categoryName } = useParams();
  const location = useLocation();
  const [filters, setFilters] = useState({
    priceRange: [0, 10000],
    sizes: [],
    colors: [],
    tags: [],
    availability: "all",
    discount: "all",
  });

  const [sortBy, setSortBy] = useState("relevance");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Construct the URL with query parameters
        const queryParams = new URLSearchParams({
          categoryType: categoryType,
          minPrice: filters.priceRange[0],
          maxPrice: filters.priceRange[1],
          sizes: filters.sizes.join(','),
          colors: filters.colors.join(','),
          tags: filters.tags.join(','),
          availability: filters.availability,
          discount: filters.discount,
          sortBy: sortBy
        });

        // Only add categoryName if it exists
        if (categoryName) {
          queryParams.append('categoryName', categoryName);
        }

        const response = await fetch(`${API_BASE_URL}/products/filter?${queryParams}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryType, categoryName, filters, sortBy]);

  // Filter sections configuration
  const filterSections = [
    {
      title: "PRICE",
      type: "range",
      component: (
        <div className="mb-4">
          <input
            type="range"
            min="0"
            max="10000"
            value={filters.priceRange[1]}
            onChange={(e) =>
              setFilters({
                ...filters,
                priceRange: [filters.priceRange[0], parseInt(e.target.value)],
              })
            }
            className="w-full accent-purple-600 h-[2px]"
          />
          <div className="flex items-center gap-2 text-sm mt-2">
            <input
              type="text"
              value={filters.priceRange[0]}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  priceRange: [
                    parseInt(e.target.value) || 0,
                    filters.priceRange[1],
                  ],
                })
              }
              className="w-[72px] border border-gray-300 py-1 px-2 text-center"
              placeholder="Min"
            />
            <span className="text-gray-400">to</span>
            <input
              type="text"
              value={filters.priceRange[1]}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  priceRange: [
                    filters.priceRange[0],
                    parseInt(e.target.value) || 0,
                  ],
                })
              }
              className="w-[72px] border border-gray-300 py-1 px-2 text-center"
              placeholder="Max"
            />
          </div>
        </div>
      ),
    },
    {
      title: "SIZE",
      type: "checkbox",
      options: ["XS", "S", "M", "L", "XL", "XXL"],
      component: (
        <div className="flex flex-wrap gap-2">
          {["XS", "S", "M", "L", "XL", "XXL"].map((size) => (
            <label key={size} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.sizes.includes(size)}
                onChange={(e) => {
                  const newSizes = e.target.checked
                    ? [...filters.sizes, size]
                    : filters.sizes.filter((s) => s !== size);
                  setFilters({ ...filters, sizes: newSizes });
                }}
                className="accent-purple-600"
              />
              <span className="text-sm">{size}</span>
            </label>
          ))}
        </div>
      ),
    },
    {
      title: "AVAILABILITY",
      type: "radio",
      component: (
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="availability"
              value="all"
              checked={filters.availability === "all"}
              onChange={(e) =>
                setFilters({ ...filters, availability: e.target.value })
              }
              className="accent-purple-600"
            />
            <span className="text-sm">All Items</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="availability"
              value="inStock"
              checked={filters.availability === "inStock"}
              onChange={(e) =>
                setFilters({ ...filters, availability: e.target.value })
              }
              className="accent-purple-600"
            />
            <span className="text-sm">In Stock Only</span>
          </label>
        </div>
      ),
    },
    {
      title: "DISCOUNT",
      type: "radio",
      component: (
        <div className="space-y-2">
          {[
            "All",
            "10% or more",
            "20% or more",
            "30% or more",
            "50% or more",
          ].map((discount) => (
            <label key={discount} className="flex items-center gap-2">
              <input
                type="radio"
                name="discount"
                value={discount}
                checked={filters.discount === discount}
                onChange={(e) =>
                  setFilters({ ...filters, discount: e.target.value })
                }
                className="accent-purple-600"
              />
              <span className="text-sm">{discount}</span>
            </label>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="w-full max-w-[85rem] mx-auto px-6 pt-6">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm mb-8">
        <Link to="/" className="text-gray-500 hover:text-gray-800">
          Home
        </Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-800">{categoryType}</span>
        {categoryName && (
          <>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-800">{categoryName}</span>
          </>
        )}
      </div>

      <div className="flex gap-8">
        {/* Sidebar filters */}
        <div className="w-[240px] shrink-0">
          <h2 className="text-sm font-medium mb-8">FILTER BY</h2>

          {filterSections.map((section) => (
            <div
              key={section.title}
              className="mb-6 pb-6 border-b border-gray-200"
            >
              <h3 className="text-[13px] font-medium mb-4">{section.title}</h3>
              {section.component}
            </div>
          ))}
        </div>

        {/* Products Section */}
        <div className="flex-1">
          {/* Results and Sort */}
          <div className="flex justify-between items-center mb-8">
            <p className="text-sm text-gray-600">{products.length} Results</p>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">SORT BY :</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 py-1 px-2 text-sm min-w-[120px]"
              >
                <option value="relevance">Relevance</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="discount">Discount</option>
                <option value="newest">Newest First</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No products found</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-6">
              {products.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductList;
